import { renderSharedHeader, initializeModalFocusManagement, handleManagerAuth, initializeChangePasswordMenu } from './components/sharedHeader.js';
import { initializeAgentAccessibility } from '../agent/integration.js';
import { getManagerID, getManagerToken, getLanguagePreference, getManagerAuthorization } from './utils/localStorageManager.js';
import { getManageCountry, getCities, getManageCity, updateManageableCities } from './utils/cityLoader.js';
import { getDefaultCountryID } from './utils/countryDefault.js';
import { initPageTranslation, t } from './language/pageTranslator.js';
import {
    disableTrainingMode,
    enableTrainingMode,
    ensureTrainingSession,
    initializeTrainingModeRuntime,
    isTrainingMode
} from './training/trainingMode.js';

initializeTrainingModeRuntime();

// ──────────────────────────────
// 側邊欄狀態常量
// ──────────────────────────────
const SIDEBAR_COLLAPSED_KEY = 'sidebarCollapsed';

// ──────────────────────────────
// 通用 Modal 焦點管理
// ──────────────────────────────

/**
 * 為所有 Bootstrap Modal 添加通用的焦點管理，解決 aria-hidden 警告
 * 此函數會被所有頁面使用，確保 Modal 關閉時不會出現無障礙性警告
 */
renderSharedHeader();

// 初始化漢堡選單按鈕事件監聽器
const navbarToggler = document.querySelector('.navbar-toggler');
if (navbarToggler) {
    navbarToggler.addEventListener('click', () => {
        const sidebar = document.getElementById('sidebarOffcanvas');
        if (sidebar) {
            sidebar.classList.toggle('show');
        }
    });
}

/**
 * 處理管理員認證和 UI 狀態
 * 根據登入狀態更新介面顯示
 */
function initializeTrainingModeIndexSwitch() {
    const switchInput = document.getElementById('trainingModeSwitch');
    const switchContainer = document.getElementById('training-mode-index-control');
    if (!switchInput || !switchContainer) return;

    switchInput.checked = isTrainingMode();
    switchContainer.classList.toggle('is-training-active', isTrainingMode());

    switchInput.addEventListener('change', () => {
        switchInput.disabled = true;
        if (switchInput.checked) {
            enableTrainingMode();
        } else {
            disableTrainingMode();
        }
        window.location.href = 'index.html';
    });
}

/**
 * 初始化側邊欄
 * 設置側邊欄的內容和結構
 */
function initializeSidebar() {
    const sidebarContainer = document.getElementById('sidebar-container');
    if (!sidebarContainer) return;

    // 檢查管理員權限，決定是否顯示超級管理員專用導航項目
    const managerAuthorization = getManagerAuthorization();
    const isSuperAdmin = managerAuthorization === '1';

    sidebarContainer.innerHTML = `
<div class="sidebar offcanvas-lg offcanvas-start" tabindex="-1" id="sidebarOffcanvas"
    aria-labelledby="sidebarOffcanvasLabel">
    <div class="offcanvas-header d-lg-none">
        <h5 class="offcanvas-title text-white" id="sidebarOffcanvasLabel">Menu</h5>
        <button type="button" class="btn-close btn-close-white" data-bs-dismiss="offcanvas"
            data-bs-target="#sidebarOffcanvas" aria-label="Close"></button>
    </div>
    <div class="offcanvas-body">
        <ul class="nav flex-column">
            <!-- Quick setup navigation is withheld pending testing; the page remains available. -->
            <li class="nav-item">
                <a class="nav-link text-white" href="review.html">
                    照片驗證
                </a>
            </li>
            <li class="nav-item">
                <a class="nav-link text-white" href="group.html">
                    分組
                </a>
            </li>
            <li class="nav-item">
                <a class="nav-link text-white" href="feedback.html">
                    回饋
                </a>
            </li>
            <li class="nav-item">
                <a class="nav-link text-white" href="users.html">
                    用戶
                </a>
            </li>
            <li class="nav-item">
                <a class="nav-link text-white" href="restaurant.html">
                    餐廳
                </a>
            </li>
            <li class="nav-item">
                <a class="nav-link text-white" href="meetups.html">
                    聚會管理
                </a>
            </li>
            <li class="nav-item">
                <a class="nav-link text-white" href="notification.html">
                    通知與推播
                </a>
            </li>
            ${isSuperAdmin ? `<li class="nav-item">
                <a class="nav-link text-white" href="edm.html">
                    EDM
                </a>
            </li>` : ''}
            <li class="nav-item">
                <a class="nav-link text-white" href="promocode.html">
                    優惠碼
                </a>
            </li>
            <li class="nav-item">
                <a class="nav-link text-white" href="seasonpass.html">
                    季票
                </a>
            </li>
            <li class="nav-item">
                <a class="nav-link text-white" href="billing.html">
                    帳務資訊
                </a>
            </li>
            ${isSuperAdmin ? `<li class="nav-item">
                <a class="nav-link text-white" href="analyze.html">
                    分析
                </a>
            </li>` : ''}
            ${isSuperAdmin ? `<li class="nav-item"><a class="nav-link text-white" href="countrycity.html">國家與城市管理</a></li>` : ''}
            ${isSuperAdmin ? `<li class="nav-item">
                <a class="nav-link text-white" href="manager.html">
                    管理員
                </a>
            </li>` : ''}
            ${managerAuthorization === '1' ? `<li class="nav-item"><a class="nav-link text-white" href="deeplink.html">DeepLink</a></li>` : ''}
        </ul>
    </div>
    <div class="sidebar-toggle-container">
        <button id="sidebarToggle" class="btn btn-sm btn-outline-light"></button>
    </div>
</div>
    `;

    highlightCurrentPage(sidebarContainer);
    initializeSidebarToggle(); // 初始化側邊欄收合功能
}

/**
 * 初始化側邊欄收合/展開功能
 */
function initializeSidebarToggle() {
    const sidebar = document.getElementById('sidebarOffcanvas');
    const content = document.querySelector('.content'); // 主內容區域
    const toggleButton = document.getElementById('sidebarToggle');
    const sidebarNav = sidebar ? sidebar.querySelector('.nav') : null; // 獲取側邊欄的 ul.nav

    if (!sidebar || !content || !toggleButton || !sidebarNav) {
        console.warn('Sidebar, content, toggle button, or sidebar navigation not found for toggle functionality.');
        return;
    }

    // 讀取 localStorage 中的狀態
    const isCollapsed = localStorage.getItem(SIDEBAR_COLLAPSED_KEY) === 'true';

    // 更新按鈕圖標和初始狀態
    function updateToggleButtonIcon(collapsed) {
        toggleButton.innerHTML = collapsed ? '<i class="bi bi-arrow-bar-right"></i>' : '<i class="bi bi-arrow-bar-left"></i>';
    }

    function applySidebarState(collapsed) {
        if (collapsed) {
            sidebar.classList.add('collapsed');
            content.classList.add('sidebar-collapsed');
            sidebarNav.classList.add('d-none'); // Bootstrap class to hide nav
        } else {
            sidebar.classList.remove('collapsed');
            content.classList.remove('sidebar-collapsed');
            sidebarNav.classList.remove('d-none'); // Bootstrap class to show nav
        }
        updateToggleButtonIcon(collapsed);
    }

    // 初始應用狀態
    applySidebarState(isCollapsed);

    toggleButton.addEventListener('click', () => {
        const currentlyCollapsed = sidebar.classList.contains('collapsed');
        applySidebarState(!currentlyCollapsed);
        // 保存狀態到 localStorage
        localStorage.setItem(SIDEBAR_COLLAPSED_KEY, !currentlyCollapsed);
    });
}

/**
 * 高亮顯示當前頁面的導航項目
 * @param {HTMLElement} sidebarContainer - 側邊欄容器元素
 */
function highlightCurrentPage(sidebarContainer) {
    const currentPage = sidebarContainer.dataset.currentPage;
    document.querySelectorAll('#sidebar-container .nav-item a').forEach(link => {
        if (link.getAttribute('href') === currentPage) {
            link.classList.add('active');
        } else {
            link.classList.remove('active');
        }
    });
}

/**
 * 初始化手機版功能區折疊。
 * 各頁用 data-mobile-collapse-title opt-in；桌面版維持原本排版。
 */
function initializeMobileCollapsibleSections() {
    const sections = document.querySelectorAll('[data-mobile-collapse-title]');
    if (sections.length === 0) return;

    const mobileQuery = window.matchMedia('(max-width: 768px)');

    sections.forEach((section, index) => {
        if (section.dataset.mobileCollapseInitialized === 'true') return;

        section.dataset.mobileCollapseInitialized = 'true';
        section.classList.add('mobile-collapsible-section');

        if (!section.id) {
            section.id = `mobileCollapseSection${index + 1}`;
        }

        const title = section.dataset.mobileCollapseTitle || '功能區';
        const defaultOpen = section.dataset.mobileCollapseDefault === 'open';
        const toggle = document.createElement('button');
        toggle.type = 'button';
        toggle.className = 'mobile-collapse-title collapsed';
        toggle.setAttribute('aria-controls', section.id);
        toggle.setAttribute('aria-expanded', 'false');
        toggle.innerHTML = `
            <i class="bi bi-caret-down-fill collapse-icon" aria-hidden="true"></i>
            <span>${title}</span>
        `;

        section.parentNode.insertBefore(toggle, section);

        const applyState = (expanded) => {
            section.classList.toggle('is-mobile-collapsed', !expanded);
            toggle.classList.toggle('collapsed', !expanded);
            toggle.setAttribute('aria-expanded', expanded ? 'true' : 'false');
        };

        applyState(!mobileQuery.matches || defaultOpen);

        toggle.addEventListener('click', () => {
            applyState(section.classList.contains('is-mobile-collapsed'));
        });

        mobileQuery.addEventListener('change', (event) => {
            if (event.matches) {
                applyState(defaultOpen);
            } else {
                applyState(true);
            }
        });
    });
}

/**
 * 初始化國家選擇下拉選單
 * 載入可管理的國家列表並設置事件監聽器
 */
function initializeCountrySelect() {
    const countrySelect = document.getElementById('countrySelect');
    if (countrySelect) {
        const countries = getManageCountry();
        if (countries && countries.length > 0) {
            countries.forEach(country => {
                const option = document.createElement('option');
                option.value = country.countryID;
                option.textContent = country.name;
                countrySelect.appendChild(option);
            });
            // 預設選擇 Taiwan；若不可管理 Taiwan 則選第一個國家
            if (countrySelect.options.length > 0) {
                const defaultCountryID = getDefaultCountryID(countries);
                countrySelect.value = defaultCountryID;
                populateCitySelect(defaultCountryID); // 為預設國家載入城市
            }
            // 添加國家變更事件監聽器
            countrySelect.addEventListener('change', (event) => {
                populateCitySelect(event.target.value);
            });
        } else {
            // 處理沒有可用國家的情況
            const option = document.createElement('option');
            option.textContent = 'No countries available';
            option.disabled = true;
            countrySelect.appendChild(option);
        }
    }
}

/**
 * 根據選擇的國家更新城市下拉選單
 * @param {string} selectedCountryID - 選擇的國家 ID
 */
function populateCitySelect(selectedCountryID) {
    const citySelect = document.getElementById('citySelect');
    if (!citySelect) return; // 如果城市選擇器不存在，直接返回

    // 清除現有選項
    citySelect.innerHTML = '';

    if (!selectedCountryID) {
        const option = document.createElement('option');
        option.textContent = 'Please select a country first';
        option.disabled = true;
        option.value = ''; // 確保值為空
        citySelect.appendChild(option);
        citySelect.value = ''; // 明確設置值為空
        citySelect.dispatchEvent(new Event('change')); // 觸發變更事件
        return;
    }

    const allCitiesForCountry = getCities(selectedCountryID); // 獲取該國家的所有城市
    const manageableCities = getManageCity(); // 獲取可管理的城市列表

    const manageableCityIDs = new Set(manageableCities.map(city => String(city.cityID)));

    const filteredCities = allCitiesForCountry.filter(city => manageableCityIDs.has(String(city.cityID)));

    if (filteredCities.length > 0) {
        filteredCities.forEach(city => {
            const option = document.createElement('option');
            option.value = city.cityID;
            
            // 如果 districts 不為空，則加在 name 後面，格式為「城市名 - 區域名」
            let displayName = city.name;
            if (city.districts && city.districts.trim() !== '') {
                displayName = `${city.name} - ${city.districts}`;
            }
            option.textContent = displayName;
            
            // 在 option 中埋入 language 屬性，以備日後使用
            if (city.language) {
                option.setAttribute('data-language', city.language);
            }
            
            citySelect.appendChild(option);
        });
        // 預設選擇第一個城市
        if (citySelect.options.length > 0) {
            citySelect.value = filteredCities[0].cityID;
        }
        citySelect.dispatchEvent(new Event('change')); // 觸發變更事件
    } else {
        const option = document.createElement('option');
        option.textContent = 'No manageable cities available';
        option.disabled = true;
        option.value = ''; // 確保值為空
        citySelect.appendChild(option);
        citySelect.value = ''; // 明確設置值為空
        citySelect.dispatchEvent(new Event('change')); // 觸發變更事件
    }
}

/**
 * 檢查登入狀態
 * 如果未登入則重定向到登入頁面
 * @returns {boolean} 是否已登入
 */
function checkLoginStatus() {
    ensureTrainingSession();

    const managerID = getManagerID();
    const token = getManagerToken();

    // 如果缺少管理員 ID 或令牌，重定向到登入頁面
    if (!managerID || !token) {
        window.location.href = 'login.html';
        return false;
    }
    return true;
}

/**
 * 初始化頁面翻譯
 * 設置所有需要翻譯的元素
 */
function initializePageTranslations() {
    initPageTranslation({
        elements: {
            '#home-language-title': 'home.languageTitle',
            '#home-language-description': 'home.languageDescription',
            '#home-training-title': 'home.trainingTitle',
            '#home-training-description': 'home.trainingDescription',
            '#home-training-label': 'home.trainingLabel',
            '#home-agents-title': 'home.agentsTitle',
            '#home-agents-description': 'home.agentsDescription',
            '#home-agents-link': 'home.agentsLink',
            // 導航項目
            '#logout-button': 'common.logout',

            // 側邊欄項目
            '#sidebarOffcanvasLabel': 'common.menu',

            // 將導航連結映射到其翻譯鍵
            '#sidebar-container .nav-item a': (element, t) => {
                const href = element.getAttribute('href');
                const translationMap = {
                    'quicksetup.html': 'nav.quicksetup',
                    'review.html': 'nav.photoverification',
                    'group.html': 'nav.groups',
                    'feedback.html': 'nav.feedback',
                    'users.html': 'nav.users',
                    'restaurant.html': 'nav.restaurant',
                    'manager.html': 'nav.administrator',
                    'deeplink.html': 'nav.deeplink',
                    'meetups.html': 'workspace.meetings',
                    'countrycity.html': 'workspace.countries',
                    'notification.html': 'nav.messagesnotifications',
                    'promocode.html': 'nav.promocode',
                    'themetable.html': 'nav.themetable',
                    'seasonpass.html': 'nav.seasonpass',
                    'billing.html': 'nav.billing',
                    'analyze.html': 'nav.analyze',
                    'edm.html': 'nav.edm'
                };

                if (translationMap[href]) {
                    element.textContent = t(translationMap[href]);
                }
            }
        }
    });
}

// 當 DOM 載入完成時初始化頁面
document.addEventListener('DOMContentLoaded', async function () {
    // 首先檢查登入狀態
    if (!checkLoginStatus()) return;

    // 初始化通用 Modal 焦點管理（適用於所有頁面）
    initializeModalFocusManagement();

    handleManagerAuth();
    initializeChangePasswordMenu();
    initializeSidebar();
    initializeMobileCollapsibleSections();
    
    // 確保 DOM 完全加載後再初始化依賴 content 元素的組件
    if (document.querySelector('.content')) {
        initializeCountrySelect();
    } else {
        console.warn("Content element not found for country select initialization.");
    }
    initializeTrainingModeIndexSwitch();
    initializePageTranslations(); // 翻譯應在所有動態內容添加後

    // 最後，在背景例行性更新可管理的城市資料
    await updateManageableCities();
});

initializeAgentAccessibility();
